import type { TFunction } from 'i18next';

import { ApiError } from './api';

/**
 * An error code becomes words here and nowhere else.
 *
 * An unknown code still renders — as itself. That is deliberate: a raw
 * SHOUTING_SNAKE_CASE string in the UI is ugly enough to get reported, where a
 * generic "something went wrong" would hide the gap forever.
 */
export function messageForCode(t: TFunction, code: string): string {
  const key = `errors.${code}` as Parameters<TFunction>[0];
  const translated = t(key);
  return translated === key ? code : (translated as string);
}

/**
 * Anything that is not an ApiError renders as the generic code.
 *
 * It used to fall back to `error.message`, which is how a library's own English
 * prose — "Request failed with status code 401" — got painted onto an Arabic
 * form. A thrown Error's message is untranslated by definition and is never
 * addressed to a reader; rule 1 has no exception for it. The `code` path above
 * is the only one that produces words a person should see.
 */
export function messageForError(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) return messageForCode(t, error.code);
  return messageForCode(t, 'INTERNAL_ERROR');
}

/** Per-field codes, translated, for rendering under an input. */
export function fieldMessages(t: TFunction, error: unknown, field: string): string[] {
  if (!(error instanceof ApiError)) return [];
  return error.fieldCodes(field).map((code) => {
    // Some field codes arrive with a payload — `AFFECTS_ITEMS:12` — where the
    // part after the colon is a count, not part of the code.
    const [bare] = code.split(':');
    return messageForCode(t, bare ?? code);
  });
}

/** Reads the counts the server attaches to a confirmable refusal. */
export function countsFromError(error: unknown, field: string): Record<string, number> {
  if (!(error instanceof ApiError)) return {};
  const out: Record<string, number> = {};
  for (const entry of error.fieldCodes(field)) {
    const [name, value] = entry.split(':');
    if (name && value !== undefined && Number.isFinite(Number(value))) out[name] = Number(value);
  }
  return out;
}

export { ApiError };
